import type {
  OutreachSuppressionInsert,
  OutreachSuppressionReason,
  OutreachSuppressionSafe,
  OutreachSuppressionSource,
  OutreachSuppressionTargetType,
} from '@/types/leads';

export const OUTREACH_SUPPRESSION_SHARED_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'ymail.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'hey.com',
]);

export class OutreachSuppressionValidationError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'OutreachSuppressionValidationError';
    this.code = code;
  }
}

function nullableText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed || null;
}

export function normalizeOutreachSuppressionEmail(value: string | null | undefined): string | null {
  const trimmed = nullableText(value);
  if (!trimmed) return null;
  const normalized = trimmed.toLowerCase();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(normalized)) {
    throw new OutreachSuppressionValidationError(
      'invalid_email',
      'Use a complete email address, such as person@example.com.',
    );
  }
  return normalized;
}

export function normalizeOutreachSuppressionLicense(value: string | null | undefined): string | null {
  const trimmed = nullableText(value);
  return trimmed ? trimmed.toLowerCase() : null;
}

export function normalizeOutreachSuppressionDomain(value: string | null | undefined): string | null {
  const trimmed = nullableText(value);
  if (!trimmed) return null;
  return (
    trimmed
      .toLowerCase()
      .replace(/^mailto:/, '')
      .replace(/^[^@]+@/, '')
      .replace(/^https?:\/\/(www\.)?/, '')
      .replace(/[:/].*$/, '')
      .trim() || null
  );
}

export function isSharedOutreachSuppressionDomain(domain: string | null | undefined): boolean {
  const normalized = normalizeOutreachSuppressionDomain(domain);
  return Boolean(normalized && OUTREACH_SUPPRESSION_SHARED_DOMAINS.has(normalized));
}

function validateDomain(domain: string | null): void {
  if (!domain) return;
  if (!/^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(domain)) {
    throw new OutreachSuppressionValidationError(
      'invalid_domain',
      'Use an exact owned business domain, such as example.com.',
    );
  }
  if (OUTREACH_SUPPRESSION_SHARED_DOMAINS.has(domain)) {
    throw new OutreachSuppressionValidationError(
      'shared_domain_rejected',
      'Shared mailbox domains such as Gmail, Yahoo, Outlook, iCloud, and Proton cannot be used as domain-wide do-not-touch entries. Add the exact email address instead.',
    );
  }
}

export function normalizeOutreachSuppressionInsert(input: OutreachSuppressionInsert): OutreachSuppressionInsert {
  const email = normalizeOutreachSuppressionEmail(input.email);
  const domain = normalizeOutreachSuppressionDomain(input.domain);
  const licenseNumber = normalizeOutreachSuppressionLicense(input.license_number);

  const targetCount = [email, domain, licenseNumber].filter(Boolean).length;
  if (targetCount === 0) {
    throw new OutreachSuppressionValidationError(
      'missing_target',
      'Add an exact email, owned domain, or license number.',
    );
  }
  if (targetCount !== 1) {
    throw new OutreachSuppressionValidationError(
      'ambiguous_target',
      'Add one do-not-touch target per entry: email, owned domain, or license number.',
    );
  }

  validateDomain(domain);

  return {
    email,
    domain,
    license_number: licenseNumber,
    reason: input.reason,
    source: input.source,
    notes: nullableText(input.notes),
    metadata: input.metadata ?? {},
  };
}

export function redactOutreachSuppressionEmail(email: string | null | undefined): string | null {
  const normalized = normalizeOutreachSuppressionEmail(email);
  if (!normalized) return null;
  const [local, domain] = normalized.split('@');
  if (!local || !domain) return '[redacted email]';
  const prefix = local.slice(0, Math.min(2, local.length));
  return `${prefix}${local.length > 2 ? '***' : '*'}@${domain}`;
}

export function redactOutreachSuppressionLicense(license: string | null | undefined): string | null {
  const normalized = normalizeOutreachSuppressionLicense(license);
  if (!normalized) return null;
  const suffix = normalized.slice(-4);
  return normalized.length > 4 ? `...${suffix}` : `...${normalized}`;
}

function getTargetType(row: {
  email?: string | null;
  domain?: string | null;
  license_number?: string | null;
}): OutreachSuppressionTargetType {
  if (row.email) return 'email';
  if (row.domain) return 'domain';
  if (row.license_number) return 'license_number';
  return 'unknown';
}

function getTargetLabel(row: {
  email?: string | null;
  domain?: string | null;
  license_number?: string | null;
}): string {
  if (row.email) return redactOutreachSuppressionEmail(row.email) ?? '[redacted email]';
  if (row.domain) return normalizeOutreachSuppressionDomain(row.domain) ?? '[redacted domain]';
  if (row.license_number) return redactOutreachSuppressionLicense(row.license_number) ?? '[redacted license]';
  return 'Unknown target';
}

export function toSafeOutreachSuppression(row: Record<string, unknown>): OutreachSuppressionSafe {
  const notes = nullableText(row.notes as string | null);
  const targetType = getTargetType({
    email: nullableText(row.email as string | null),
    domain: nullableText(row.domain as string | null),
    license_number: nullableText(row.license_number as string | null),
  });
  return {
    id: String(row.id ?? ''),
    target_type: targetType,
    target_label: getTargetLabel({
      email: nullableText(row.email as string | null),
      domain: nullableText(row.domain as string | null),
      license_number: nullableText(row.license_number as string | null),
    }),
    reason: (row.reason as OutreachSuppressionReason | null) ?? null,
    source: (row.source as OutreachSuppressionSource | null) ?? null,
    has_notes: Boolean(notes),
    released_at: nullableText(row.released_at as string | null),
    released_by: null,
    release_reason: null,
    created_at: nullableText(row.created_at as string | null) ?? '',
    updated_at: nullableText(row.updated_at as string | null) ?? '',
  };
}
